<template>
  <UiModal
    ref="modal"
    width="600px"
    hide-footer
    disable-esc
    :hideClose="form.obrigatorio"
    @before-close="beforeClose"
  >
    <template #title>
      <div class="flex items-center">
        <UiIcon icon="clipboard" size="20px" class="mr-2" />
        <div class="text-xl font-semibold">{{ $t('wellBeingIndicator.labels.search') }}</div>
      </div>
    </template>

    <UiBadge v-if="form.obrigatorio" type="warning" class="w-full rounded-md">
      <div class="flex items-center">
        <UiIcon icon="info-triangle" size="20px" class="mr-2" />

        <div class="text-sm text-left">
          <h4 class="font-medium mb-1" v-html="$t('wellBeingIndicator.messages.requiredSurvey')" />
        </div>
      </div>
    </UiBadge>

    <UiBadge v-if="form.anonimo" type="info" class="w-full rounded-md">
      <div class="flex items-center">
        <UiIcon icon="info-circle" size="20px" class="mr-2" />

        <div class="text-sm text-left">
          <h4 class="font-medium mb-1" v-html="$t('wellBeingIndicator.messages.anonymousSurvey')" />
        </div>
      </div>
    </UiBadge>

    <UiForm
      ref="formRef"
      :form-data="form"
      :rules="rules"
      class="flex flex-col gap-4 space-y-2 font-medium text-sm"
    >
      <!-- Seleção de sentimento (SVG) -->
      <UiFormControl prop="sentimento" required :label="form.nome" class="break-all">
        <div class="grid sm:grid-cols-5 grid-cols-4 gap-2 text-black dark:text-white">
          <button
            v-for="op in opcoesSentimento"
            :key="op.value"
            type="button"
            class="flex flex-col items-center justify-center py-2 px-1 rounded-lg border transition"
            :class="
              form.sentimento === op.value
                ? 'border-[--c-primary-500] bg-primary-soft dark:bg-primary-soft-dark'
                : 'border-gray-200 dark:border-gray-300 hover:border-[--c-primary-500] dark:hover:border-[--c-primary-500] hover-primary'
            "
            @click="form.sentimento = op.value"
            @mouseover="hovered = op.value"
            @mouseleave="hovered = ''"
          >
            <img :src="getEmojiSrc(op.value)" class="w-10 h-10 z-50" alt="Emoji" />
            <span class="text-xs mt-1 z-50">{{ op.label }}</span>
          </button>
        </div>
      </UiFormControl>


      <!-- Select motivo -->
      <UiFormControl prop="motivo" required :label="$t('wellBeingIndicator.messages.reason')">
        <UiSelectNext
          v-model="form.motivo"
          :items="motivos"
          :search-threshold="4"
          :placeholder="$t('wellBeingIndicator.placeholder.reasonSelect')"
          clearable
        />
      </UiFormControl>

      <!-- Comentário -->
      <UiFormControl prop="comentario" :label="$t('wellBeingIndicator.messages.comment')">
        <UiInput
          v-model="form.comentario"
          type="textarea"
          :placeholder="$t('wellBeingIndicator.placeholder.commentInput')"
          rows="4"
        />
      </UiFormControl>

      <div class="col-span-full mt-4 flex items-center justify-end gap-4">
        <UiButton
          type="primary"
          icon="send"
          class="px-8 py-4 text-sm uppercase"
          :disabled="!form.sentimento || !form.motivo"
          :loading="indicadorBemEstarRespostaStore.loading"
          @click="submit"
        >
          {{ $t('button.send') }}
        </UiButton>
      </div>
    </UiForm>
  </UiModal>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import webStorage from '@/common/webStorage'
import { i18N } from '@/common/util'
import { validationRules } from '@/common/validation'
import { useIndicadorDeBemEstarRespostaStore } from '@/stores'

import UiInput from '@/components/ui/input.vue'
import UiButton from '@/components/ui/button.vue'
import UiForm from '@/components/ui/form.vue'
import UiFormControl from '@/components/ui/formControl.vue'
import UiSelectNext from '@/components/ui/selectNext.vue'
import UiModal from '@/components/ui/modal.vue'
import UiBadge from '@/components/ui/badge.vue'
import UiIcon from '@/components/ui/icon.vue'

// Animados
import angryAnim from '@/assets/emojis/animated/angryFace.png'
import confusedAnim from '@/assets/emojis/animated/confused-face.png'
import cryingAnim from '@/assets/emojis/animated/crying-face.png'
import disappointedAnim from '@/assets/emojis/animated/disappointed-face.png'
import grinningAnim from '@/assets/emojis/animated/grinning-face-with-big-eyes.png'
import grinningSquintingAnim from '@/assets/emojis/animated/grinning-squinting-face.png'
import loudlyCryingAnim from '@/assets/emojis/animated/loudly-crying-face.png'
import neutralAnim from '@/assets/emojis/animated/neutral-face.png'
import smilingAnim from '@/assets/emojis/animated/smiling-face-with-smiling-eyes.png'
import starStruckAnim from '@/assets/emojis/animated/star-struck.png'

// Estáticos
import angryStatic from '@/assets/emojis/static/angry_face_3d.png'
import confusedStatic from '@/assets/emojis/static/confused_face_3d.png'
import cryingStatic from '@/assets/emojis/static/crying_face_3d.png'
import disappointedStatic from '@/assets/emojis/static/disappointed_face_3d.png'
import grinningStatic from '@/assets/emojis/static/grinning_face_with_big_eyes_3d.png'
import grinningSquintingStatic from '@/assets/emojis/static/grinning_squinting_face_3d.png'
import loudlyCryingStatic from '@/assets/emojis/static/loudly_crying_face_3d.png'
import neutralStatic from '@/assets/emojis/static/neutral_face_3d.png'
import smilingStatic from '@/assets/emojis/static/smiling_face_with_smiling_eyes_3d.png'
import starStruckStatic from '@/assets/emojis/static/star-struck_3d.png'

const indicadorBemEstarRespostaStore = useIndicadorDeBemEstarRespostaStore()

const defaultValues = {
  id: '',
  nome: '',
  descricao: '',
  obrigatorio: false,
  frequencia: 0,
  estilo: 'Estatico',
  anonimo: false,
  dataVisualizacao: '',
  sentimento: '',
  motivo: '',
  comentario: ''
}

const modal = ref()
const formRef = ref()
const form = reactive({ ...defaultValues })
const hovered = ref('')

const rules = validationRules([
  'sentimento|required', 
  'motivo|required', 
])

const opcoesSentimento = [
  { value: 'Raiva', label: i18N('wellBeingIndicator.emotions.anger'), anim: angryAnim, static: angryStatic },
  { value: 'MuitoTriste', label: i18N('wellBeingIndicator.emotions.verySad'), anim: loudlyCryingAnim, static: loudlyCryingStatic },
  { value: 'Triste', label: i18N('wellBeingIndicator.emotions.sad'), anim: cryingAnim, static: cryingStatic },
  { value: 'Chateado', label: i18N('wellBeingIndicator.emotions.upset'), anim: disappointedAnim, static: disappointedStatic },
  { value: 'Inseguro', label: i18N('wellBeingIndicator.emotions.insecure'), anim: confusedAnim, static: confusedStatic },
  { value: 'Indiferente', label: i18N('wellBeingIndicator.emotions.indifferent'), anim: neutralAnim, static: neutralStatic },
  { value: 'Bem', label: i18N('wellBeingIndicator.emotions.fine'), anim: grinningAnim, static: grinningStatic },
  { value: 'Feliz', label: i18N('wellBeingIndicator.emotions.happy'), anim: smilingAnim, static: smilingStatic },
  { value: 'MuitoFeliz', label: i18N('wellBeingIndicator.emotions.veryHappy'), anim: grinningSquintingAnim, static: grinningSquintingStatic },
  { value: 'Radiante', label: i18N('wellBeingIndicator.emotions.radiant'), anim: starStruckAnim, static: starStruckStatic }
]

const motivos = [
  { value: 'Pessoal', label: i18N('wellBeingIndicator.labels.guys') },
  { value: 'Profissional', label: i18N('wellBeingIndicator.labels.professional') },
  { value: 'Outro', label: i18N('labels.other') }
]

const getEmojiSrc = (value: string) => {
  const op = opcoesSentimento.find(o => o.value === value)
  if (!op) return ''

  if (form.estilo === 'Animado') return form.sentimento === value || hovered.value === value ? op.anim : op.static
  
  return op.static
}

const open = async () => {
  await modal.value?.open()

  Object.assign(form, {
      ...indicadorBemEstarRespostaStore.activeIndicator
  })
}

const beforeClose = async () => {
  if (form.obrigatorio) return

  const proxima = new Date()
  proxima.setDate(proxima.getDate() + Number(form.frequencia))
  const proximaApresentacao = proxima.toISOString()

  await webStorage.setItem('modalIndicador', JSON.stringify({ openModal: false, nextPresentation: proximaApresentacao }), true)
}

const close = () => {
  modal.value?.cancel()
}

const submit = async () => {
  if (!formRef.value.validate()) return

  const formSubmit = {
    dataVisualizacao: new Date().toISOString(),
    sentimento: form.sentimento,
    motivo: form.motivo,
    comentario: form.comentario || ''
  }

  const result = await indicadorBemEstarRespostaStore.saveAnswerIndicator(form.id, formSubmit)
  result && close()
}

defineExpose({
  open,
  close
})
</script>

<style scoped>
  .bg-primary-soft,
  .bg-primary-soft-dark,
  .hover-primary {
    @apply relative;
  }

  .bg-primary-soft::before,
  .bg-primary-soft-dark::before,
  .hover-primary:hover::before {
    content: '';
    @apply absolute inset-0 z-10 bg-[var(--c-primary-500)];
  }

  .bg-primary-soft::before,
  .hover-primary:hover::before {
    opacity: 0.15;
  }

  .bg-primary-soft-dark::before,
  .dark .hover-primary:hover::before {
    opacity: 0.25;
  }
</style>